/**
 * Decodes the SERPRO CNH-e facial photo (a BPG image with the first three
 * magic bytes "BPG" stripped — the payload starts at the trailing 0xFB).
 * Reconstructs a standard BPG stream and decodes it with Fabrice Bellard's
 * libbpg (vendored asm.js build).
 *
 * The decoder is served as a static script from `public/bpg/bpgdec8a.js` and
 * loaded on demand. It is NOT bundled: its Emscripten glue uses direct `eval`,
 * which an esbuild-minified bundle would break, so it must run as a plain
 * (un-renamed) classic script.
 *
 * Returns a PNG data URL, or null if decoding fails.
 */
interface BpgDecoderInstance {
  onload?: () => void;
  imageData?: ImageData;
  bpg_decoder_open(): number;
  bpg_decoder_close(img: number): void;
  _onload(request: { response: ArrayBuffer }, event?: unknown): void;
}
type BpgDecoderCtor = new (ctx: CanvasRenderingContext2D) => BpgDecoderInstance;

declare global {
  interface Window {
    BPGDecoder8a?: BpgDecoderCtor;
  }
}

const SCRIPT_URL = 'assets/brdocs/bpg/bpgdec8a.js';
let scriptPromise: Promise<BpgDecoderCtor> | null = null;

function loadDecoder(): Promise<BpgDecoderCtor> {
  if (window.BPGDecoder8a) {
    return Promise.resolve(window.BPGDecoder8a);
  }
  if (!scriptPromise) {
    scriptPromise = new Promise<BpgDecoderCtor>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        if (window.BPGDecoder8a) {
          resolve(window.BPGDecoder8a);
        } else {
          reject(new Error('BPG decoder script loaded but global is missing'));
        }
      };
      script.onerror = () => reject(new Error('Failed to load BPG decoder script'));
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/** The asm.js runtime initialises asynchronously; probe until it's callable. */
async function waitReady(inst: BpgDecoderInstance): Promise<void> {
  for (let i = 0; i < 200; i++) {
    try {
      const p = inst.bpg_decoder_open();
      inst.bpg_decoder_close(p);
      return;
    } catch {
      await new Promise((r) => setTimeout(r, 15));
    }
  }
  throw new Error('BPG decoder runtime did not initialise');
}

export async function bpgToDataUrl(serproBytes: Uint8Array): Promise<string | null> {
  try {
    const Ctor = await loadDecoder();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    const inst = new Ctor(ctx);
    await waitReady(inst);

    // Reconstruct a standard BPG stream: prepend "BPG" (0x42 0x50 0x47).
    // The SERPRO bytes already begin with the magic's trailing 0xFB.
    const bpg = new Uint8Array(3 + serproBytes.length);
    bpg.set([0x42, 0x50, 0x47]);
    bpg.set(serproBytes, 3);

    // Drive the decoder's internal handler directly instead of its XHR loader.
    inst._onload({ response: bpg.buffer.slice(bpg.byteOffset, bpg.byteOffset + bpg.byteLength) });

    const im = inst.imageData;
    if (!im) {
      return null;
    }
    canvas.width = im.width;
    canvas.height = im.height;
    ctx.putImageData(im, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
